import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  FiShoppingBag, 
  FiDollarSign, 
  FiUsers, 
  FiPackage,
  FiTrendingUp,
  FiArrowUp,
  FiArrowDown,
  FiAlertCircle,
  FiClock,
  FiShoppingCart,
  FiStar
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import api from '../../api/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    totalCustomers: 0,
    totalProducts: 0,
    lowStockItems: 0,
    pendingOrders: 0,
    avgOrderValue: 0,
    customerRetention: 0,
  });

  // Add the missing metrics state
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    averageOrderValue: 0,
    customerRetention: 0,
    salesTrends: [],
    customerMetrics: {
      totalCustomers: 0,
      repeatCustomers: 0
    }
  });

  const [isLoading, setIsLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [popularProducts, setPopularProducts] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [recentOrders, setRecentOrders] = useState([]);

  const fetchCategoryDistribution = async () => {
    setIsCategoryLoading(true);
    try {
      // Fetch both categories and products
      const [categoriesData, productsData] = await Promise.all([
        api.getCategories(),
        api.getProducts()
      ]);

      // Ensure we have arrays
      const categories = Array.isArray(categoriesData) 
        ? categoriesData 
        : (categoriesData?.results || []);
      
      const products = Array.isArray(productsData) 
        ? productsData 
        : (productsData?.results || []);

      // Filter active categories and calculate product count
      const distribution = categories
        .filter(category => category.is_active) // Only include active categories
        .map(category => ({
          name: category.name,
          value: products.filter(product => 
            product.category?.id === category.id || 
            product.category?.name === category.name
          ).length,
          percentage: 0 // We'll calculate this below
        }))
        // Sort by product count in descending order
        .sort((a, b) => b.value - a.value);

      // Calculate total products across all active categories
      const totalProducts = distribution.reduce((sum, cat) => sum + cat.value, 0);

      // Calculate percentage for each category
      const distributionWithPercentage = distribution.map(cat => ({
        ...cat,
        percentage: totalProducts > 0 
          ? ((cat.value / totalProducts) * 100).toFixed(1) 
          : 0
      }));

      setCategoryDistribution(distributionWithPercentage);

    } catch (error) {
      console.error('Error fetching category distribution:', error);
      toast.error('Failed to load category distribution');
    } finally {
      setIsCategoryLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await api.getAllOrders().catch(async () => {
          return await api.getOrders();
        });

        const ordersArray = Array.isArray(response) ? response : (response?.results || []);

        // Filter completed payments
        const completedOrders = ordersArray.filter(order => 
          order.payment_status?.toLowerCase() === 'completed' || 
          order.paymentStatus?.toLowerCase() === 'completed'
        );

        // Calculate totals from completed orders
        const totalSales = completedOrders.reduce((sum, order) => 
          sum + parseFloat(order.total_amount || order.total || 0), 0
        );

        const totalOrders = completedOrders.length;
        
        // Calculate average order value
        const avgOrderValue = totalOrders > 0 
          ? parseFloat((totalSales / totalOrders).toFixed(2))
          : 0;

        setStats({
          totalSales: totalSales,
          totalOrders: totalOrders,
          avgOrderValue: avgOrderValue,
          totalCustomers: stats.totalCustomers,
          totalProducts: stats.totalProducts,
          lowStockItems: stats.lowStockItems,
          pendingOrders: stats.pendingOrders,
          customerRetention: stats.customerRetention
        });

        // Also update metrics for additional stats display
        setMetrics(prevMetrics => ({
          ...prevMetrics,
          averageOrderValue: avgOrderValue
        }));

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    fetchRecentOrders();
  }, []);

  const fetchRecentOrders = async () => {
    setIsLoadingOrders(true);
    try {
      let ordersData = await api.getAllOrders().catch(async () => {
        return await api.getOrders();
      });

      const ordersArray = Array.isArray(ordersData) ? ordersData : (ordersData?.results || []);

      const sortedOrders = ordersArray
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      const transformedOrders = sortedOrders.map(order => {
        // Get customer name from shipping address
        let customerName = 'Anonymous';
        
        if (order.shipping_address) {
          const { first_name, last_name } = order.shipping_address;
          if (first_name || last_name) {
            customerName = `${first_name || ''} ${last_name || ''}`.trim();
          }
        }

        return {
          id: order.order_id || order.id,
          orderNumber: order.order_number,
          customer: customerName,
          items: Array.isArray(order.items) 
            ? order.items.length 
            : (Array.isArray(order.order_items) 
              ? order.order_items.length 
              : 0),
          total: `₹${parseFloat(order.total_amount || order.total || 0).toLocaleString()}`,
          status: order.status || 'Processing',
          date: format(new Date(order.created_at), 'MMM dd, yyyy'),
          time: format(new Date(order.created_at), 'HH:mm')
        };
      });

      setRecentOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      toast.error('Failed to load recent orders');
      setRecentOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    const fetchSalesData = async () => {
      setSalesLoading(true);
      try {
        const response = await api.getAllOrders().catch(async () => {
          return await api.getOrders();
        });

        const ordersArray = Array.isArray(response) ? response : (response?.results || []);

        // Initialize all 12 months with 0 values
        const initialMonths = {
          'Jan': { name: 'Jan', sales: 0, orders: 0 },
          'Feb': { name: 'Feb', sales: 0, orders: 0 },
          'Mar': { name: 'Mar', sales: 0, orders: 0 },
          'Apr': { name: 'Apr', sales: 0, orders: 0 },
          'May': { name: 'May', sales: 0, orders: 0 },
          'Jun': { name: 'Jun', sales: 0, orders: 0 },
          'Jul': { name: 'Jul', sales: 0, orders: 0 },
          'Aug': { name: 'Aug', sales: 0, orders: 0 },
          'Sep': { name: 'Sep', sales: 0, orders: 0 },
          'Oct': { name: 'Oct', sales: 0, orders: 0 },
          'Nov': { name: 'Nov', sales: 0, orders: 0 },
          'Dec': { name: 'Dec', sales: 0, orders: 0 }
        };

        // Process orders and aggregate by month (only completed payments)
        const salesByMonth = ordersArray.reduce((acc, order) => {
          // Check if payment is completed
          const isPaymentCompleted = 
            order.payment_status?.toLowerCase() === 'completed' || 
            order.paymentStatus?.toLowerCase() === 'completed';

          if (isPaymentCompleted) {
            const orderDate = new Date(order.created_at);
            const monthKey = format(orderDate, 'MMM');
            
            if (acc[monthKey]) {
              const amount = parseFloat(order.total_amount || order.total || 0);
              acc[monthKey].sales += amount;
              acc[monthKey].orders += 1;
            }
          }
          
          return acc;
        }, initialMonths);

        // Convert to array and ensure Jan-Dec order
        const monthOrder = [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        const transformedData = monthOrder.map(month => salesByMonth[month]);

        setSalesData(transformedData);
      } catch (error) {
        console.error('Error fetching sales data:', error);
        toast.error('Failed to load sales data');
        setSalesData([
          { name: 'Jan', sales: 0, orders: 0 },
          { name: 'Feb', sales: 0, orders: 0 },
          { name: 'Mar', sales: 0, orders: 0 },
          { name: 'Apr', sales: 0, orders: 0 },
          { name: 'May', sales: 0, orders: 0 },
          { name: 'Jun', sales: 0, orders: 0 },
          { name: 'Jul', sales: 0, orders: 0 },
          { name: 'Aug', sales: 0, orders: 0 },
          { name: 'Sep', sales: 0, orders: 0 },
          { name: 'Oct', sales: 0, orders: 0 },
          { name: 'Nov', sales: 0, orders: 0 },
          { name: 'Dec', sales: 0, orders: 0 }
        ]);
      } finally {
        setSalesLoading(false);
      }
    };

    fetchSalesData();
  }, []);

  // Helper function to format currency in Indian format
  const formatIndianCurrency = (amount) => {
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter.format(amount);
  };

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'shipped': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'completed': 'bg-green-100 text-green-800'
    };
    return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const RecentOrdersSection = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Recent Orders</h2>
        <button 
          onClick={fetchRecentOrders}
          className="text-indigo-600 hover:text-indigo-800"
        >
          Refresh
        </button>
      </div>
      
      {isLoadingOrders ? (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      ) : recentOrders.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No recent orders found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Order ID</th>
                <th className="text-left py-3 px-4">Customer</th>
                <th className="text-left py-3 px-4">Items</th>
                <th className="text-left py-3 px-4">Total</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((order) => (
                <tr key={order.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">
                    <div>#{order.id}</div>
                    <div className="text-xs text-gray-500">{order.orderNumber}</div>
                  </td>
                  <td className="py-3 px-4">{order.customer}</td>
                  <td className="py-3 px-4">{order.items} items</td>
                  <td className="py-3 px-4 font-medium">{order.total}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm">
                      <div>{order.date}</div>
                      <div className="text-gray-500">{order.time}</div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSalesChart = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Sales Overview</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
            <span className="text-sm text-gray-600">Completed Sales</span>
          </div>
        </div>
      </div>
      <div className="h-80">
        {salesLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={salesData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#E5E7EB" 
                vertical={false}
              />
              <XAxis 
                dataKey="name" 
                stroke="#9CA3AF"
                axisLine={false}
                tickLine={false}
                dy={10}
                tick={{
                  fill: '#6B7280',
                  fontSize: 12
                }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                stroke="#9CA3AF"
                axisLine={false}
                tickLine={false}
                dx={-10}
                tick={{
                  fill: '#6B7280',
                  fontSize: 12
                }}
                tickFormatter={(value) => `₹${value.toLocaleString('en-IN')}`}
                domain={[0, 'dataMax + 1000']}
                allowDataOverflow={false}
              />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-100">
                        <p className="text-sm text-gray-600 mb-2">{label}</p>
                        <p className="text-lg font-semibold text-indigo-600">
                          {formatIndianCurrency(payload[0].value)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {payload[0].payload.orders} completed orders
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#4F46E5"
                strokeWidth={2}
                fill="url(#salesGradient)"
                name="Sales"
                dot={{
                  stroke: '#4F46E5',
                  strokeWidth: 2,
                  fill: '#FFFFFF',
                  r: 4
                }}
                activeDot={{
                  stroke: '#4F46E5',
                  strokeWidth: 2,
                  fill: '#4F46E5',
                  r: 6
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  // Add a helper function for formatting currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    })}`;
  };

  const fetchLowStockItems = async () => {
    try {
      // Fetch products
      const response = await api.getProducts();
      const products = Array.isArray(response) ? response : (response?.results || []);
      
      // Calculate low stock items (items with total stock ≤ 5)
      const lowStockItems = products.filter(product => {
        const totalStock = product.variants?.reduce((sum, variant) => {
          return sum + (parseInt(variant.stock) || 0);
        }, 0) || 0;
        return totalStock <= 5;
      });

      // Update the low stock count
      setLowStockCount(lowStockItems.length);

      // Update the stats object with the new count
      setStats(prevStats => ({
        ...prevStats,
        lowStockItems: lowStockItems.length
      }));

    } catch (error) {
      console.error('Error fetching low stock items:', error);
      toast.error('Failed to load low stock items');
      setLowStockCount(0);
    }
  };

  useEffect(() => {
    fetchLowStockItems();
  }, []); // Empty dependency array means this runs once when component mounts

  useEffect(() => {
    fetchCategoryDistribution();
  }, []);

  const renderCategoryDistribution = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Active Category Distribution</h2>
        <span className="text-sm text-gray-500">
          Total Categories: {categoryDistribution.length}
        </span>
      </div>
      
      {isCategoryLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : categoryDistribution.length > 0 ? (
        <div className="space-y-4">
          {categoryDistribution.map((category) => (
            <div key={category.name} className="relative">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {category.name}
                </span>
                <span className="text-sm text-gray-600">
                  {category.value} products ({category.percentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-indigo-600 h-2.5 rounded-full"
                  style={{ width: `${category.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No active categories found
        </div>
      )}
    </div>
  );

  // Add this function to fetch and process popular products
  const fetchPopularProducts = async () => {
    try {
      // Fetch orders and products
      const [ordersResponse, productsResponse] = await Promise.all([
        api.getAllOrders().catch(() => api.getOrders()),
        api.getProducts()
      ]);

      const orders = Array.isArray(ordersResponse) ? ordersResponse : (ordersResponse?.results || []);
      const products = Array.isArray(productsResponse) ? productsResponse : (productsResponse?.results || []);

      // Filter completed orders
      const completedOrders = orders.filter(order => 
        order.payment_status?.toLowerCase() === 'completed' || 
        order.paymentStatus?.toLowerCase() === 'completed'
      );

      // Create a map to track product sales count only
      const productSales = {};

      // Count sales for each product from completed orders
      completedOrders.forEach(order => {
        const orderItems = order.items || order.order_items || [];
        
        orderItems.forEach(item => {
          const productId = item.product_id || item.productId || item.product?.id;
          if (!productSales[productId]) {
            productSales[productId] = { sales: 0 };
          }
          const quantity = parseInt(item.quantity) || 1;
          productSales[productId].sales += quantity;
        });
      });

      // Map product details with sales data
      const productsWithSales = products.map(product => {
        const sales = productSales[product.id] || { sales: 0 };
        const totalStock = product.variants?.reduce((sum, variant) => 
          sum + (parseInt(variant.stock) || 0), 0) || 0;

        return {
          id: product.id,
          name: product.name,
          sales: sales.sales,
          stock: totalStock,
          rating: product.rating || 0
        };
      });

      // Sort by sales and get top 4
      const topProducts = productsWithSales
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 4);

      setPopularProducts(topProducts);

    } catch (error) {
      console.error('Error fetching popular products:', error);
      toast.error('Failed to load popular products');
    }
  };

  useEffect(() => {
    fetchPopularProducts();
  }, []);

  const renderPopularProducts = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-lg font-semibold mb-4">Popular Products</h2>
      <div className="space-y-4">
        {popularProducts.map((product) => (
          <div key={product.id} className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <h3 className="font-medium">{product.name}</h3>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-sm text-gray-500">
                  Stock: {product.stock.toLocaleString()}
                </span>
                {product.rating > 0 && (
                  <span className="flex items-center text-sm text-yellow-500">
                    <FiStar className="w-4 h-4 mr-1" />
                    {product.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-6 p-6"
    >
      <Helmet>
        <title>Dashboard | Admin | Panchali Boutique</title>
      </Helmet>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={FiDollarSign}
          title="Total Sales (Completed)"
          value={formatCurrency(stats.totalSales)}
          trend={12}
          isPositive={true}
          color="bg-gradient-to-r from-blue-500 to-blue-600"
        />
        <StatCard
          icon={FiShoppingBag}
          title="Completed Orders"
          value={stats.totalOrders}
          trend={8}
          isPositive={true}
          color="bg-gradient-to-r from-green-500 to-green-600"
        />
        <StatCard
          icon={FiDollarSign}
          title="Avg. Order Value"
          value={formatCurrency(stats.avgOrderValue)}
          trend={5}
          isPositive={true}
          color="bg-gradient-to-r from-purple-500 to-purple-600"
        />
        <StatCard
          icon={FiPackage}
          title="Low Stock Items"
          value={lowStockCount}
          trend={-2}
          isPositive={false}
          color="bg-gradient-to-r from-red-500 to-red-600"
          onClick={() => navigate('/admin/products?filter=lowStock')}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderSalesChart()}
        {renderCategoryDistribution()}
      </div>

      {/* Recent Orders and Popular Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentOrdersSection />
        {renderPopularProducts()}
      </div>
    </motion.div>
  );
};

const StatCard = ({ icon: Icon, title, value, trend, isPositive, color, onClick }) => (
  <div className="bg-white rounded-lg shadow p-6 cursor-pointer" onClick={onClick}>
    <div className="flex items-center justify-between">
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      {trend && (
        <span className={`flex items-center ${isPositive ? 'text-green-500' : 'text-red-500'} text-sm`}>
          {isPositive ? <FiArrowUp className="mr-1" /> : <FiArrowDown className="mr-1" />}
          {trend}%
        </span>
      )}
    </div>
    <h3 className="text-gray-500 text-sm font-medium mt-4">{title}</h3>
    <p className="text-2xl font-semibold mt-2">{value}</p>
  </div>
);

const MetricCard = ({ icon: Icon, title, value, description }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-gray-500" />
      <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
    </div>
    <p className="text-2xl font-semibold mt-2">{value}</p>
    <p className="text-sm text-gray-500 mt-1">{description}</p>
  </div>
);

export default Dashboard;

















